import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsIcon, GitMergeIcon, WorkflowIcon, DatabaseIcon, MessageSquareIcon, FileTextIcon, CheckCircleIcon, ArrowRightIcon, SearchIcon, MapIcon, WrenchIcon, ActivityIcon, RocketIcon, LayersIcon, CpuIcon, AlertCircleIcon, ChevronDownIcon, ZapIcon, BuildingIcon, UsersIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function CustomAutomationsPage() {
  const faqs = [{
    question: 'What kind of automations can you build for contractors?',
    answer: 'We build bespoke workflows to eliminate manual data entry. Common examples include syncing leads from ad platforms directly into your dispatch software, automatically sending estimate follow-ups, triggering review requests when a job is marked complete, and routing specific job types to specific crews.'
  }, {
    question: 'How long does a custom automation project take?',
    answer: 'Simple automations (like connecting a web form to a Google Sheet) can be done in a few days. Complex, multi-step workflows involving custom API integrations and conditional logic typically take 2-4 weeks to build, test, and deploy.'
  }, {
    question: 'What tools and platforms can you integrate?',
    answer: 'We can integrate with almost any software that has an open API. We frequently work with ServiceTitan, Jobber, Housecall Pro, GoHighLevel, Salesforce, HubSpot, QuickBooks, Stripe, Google Workspace, and major ad platforms.'
  }, {
    question: 'Do I need to know how to code?',
    answer: 'No. We handle all the technical architecture, coding, and API connections. We deliver a finished, working system and provide you with a simple dashboard to monitor it, along with training for your team.'
  }, {
    question: 'How much does a custom automation cost?',
    answer: 'Pricing depends entirely on the complexity of the workflow and the systems involved. We start with a free scoping call to understand your operational bottlenecks, then provide a flat-rate project fee.'
  }, {
    question: 'Can you automate workflows I already have in place?',
    answer: 'Yes. We often audit existing, manual workflows (like office staff copy-pasting data between spreadsheets) and build automated systems to replace them, saving hours of administrative work each week and eliminating human error.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-1319">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1320">
          <span className="text-lg font-bold text-white group-hover:text-red-500 transition-colors" data-id="element-1321">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-red-500' : ''}`} data-id="element-1322" />
        </button>
        <AnimatePresence data-id="element-1323">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1324">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1325">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="pt-24 pb-16" data-id="element-1326">
      <SEOHead title="Custom Automations & API Integrations | Viridia Analytics" description="Bespoke automation workflows for home service businesses. Connect your tools, eliminate manual data entry, and build systems tailored to your unique operations." canonical="/custom-automations" keywords="custom automations, API integrations, workflow automation, business process automation, Zapier alternative, custom webhooks, Phoenix automation agency, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1327" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-1328">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1329">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-red-500/10 blur-[120px] animate-pulse-slow" data-id="element-1330"></div>
        </div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgNDBoNDBWMEgwem0zOS0zOXYzOEgxVjFoMzh6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] -z-10 opacity-50" data-id="element-1331"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1332">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1333">
            <ScrollReveal data-id="element-1334">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-medium mb-6" data-id="element-1335">
                <SettingsIcon className="w-4 h-4" data-id="element-1336" />
                <span data-id="element-1337">Bespoke Systems Engineering</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-1338">
                Connect Your Tools. <br data-id="element-1339" />
                <span className="text-red-500" data-id="element-1340">Automate the Rest.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-1341">
                When off-the-shelf software doesn't fit your unique process, we
                build custom workflows that make your existing tools talk to
                each other. Eliminate manual data entry and operational
                bottlenecks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1342">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1343">
                  Book a Scoping Call
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="hidden lg:block" data-id="element-1344">
              <div className="relative" data-id="element-1345">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-1346">
                  {/* Workflow Diagram Visualization */}
                  <div className="flex flex-col items-center gap-4 relative z-10 py-4" data-id="element-1347">
                    {/* Trigger */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.5
                  }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg" data-id="element-1348">
                      <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1349">
                        <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-1350" />
                      </div>
                      <div className="min-w-0" data-id="element-1351">
                        <div className="text-xs text-gray-500" data-id="element-1352">Trigger</div>
                        <div className="text-sm font-medium text-white truncate" data-id="element-1353">
                          New Quote Form Submitted
                        </div>
                      </div>
                    </motion.div>

                    <div className="w-px h-8 bg-gray-700" data-id="element-1354"></div>

                    {/* Logic Branch */}
                    <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3
                  }} className="bg-viridia-black border border-viridia/30 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg relative" data-id="element-1355">
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1356"></div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1357"></div>

                      <div className="w-8 h-8 rounded bg-viridia/20 flex items-center justify-center" data-id="element-1358">
                        <CpuIcon className="w-4 h-4 text-viridia" data-id="element-1359" />
                      </div>
                      <div data-id="element-1360">
                        <div className="text-xs text-gray-500" data-id="element-1361">Action</div>
                        <div className="text-sm font-medium text-white" data-id="element-1362">
                          AI Summarizes Request
                        </div>
                      </div>
                    </motion.div>

                    <div className="flex w-full max-w-md justify-between px-12 relative" data-id="element-1363">
                      <div className="w-px h-8 bg-gray-700 absolute left-1/4 top-0" data-id="element-1364"></div>
                      <div className="w-px h-8 bg-gray-700 absolute right-1/4 top-0" data-id="element-1365"></div>
                      <div className="w-1/2 h-px bg-gray-700 absolute left-1/4 top-0" data-id="element-1366"></div>
                    </div>

                    {/* Outcomes */}
                    <div className="flex gap-4 sm:gap-8 w-full justify-center flex-wrap sm:flex-nowrap" data-id="element-1367">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.6
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1368">
                        <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center" data-id="element-1369">
                          <DatabaseIcon className="w-4 h-4 text-green-400" data-id="element-1370" />
                        </div>
                        <div data-id="element-1371">
                          <div className="text-xs text-gray-500" data-id="element-1372">CRM</div>
                          <div className="text-sm font-medium text-white" data-id="element-1373">
                            Create Record
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.8
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1374">
                        <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1375">
                          <MessageSquareIcon className="w-4 h-4 text-purple-400" data-id="element-1376" />
                        </div>
                        <div className="min-w-0" data-id="element-1377">
                          <div className="text-xs text-gray-500" data-id="element-1378">Slack</div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-1379">
                            Alert Sales Team
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Animated Pulses */}
                    <motion.div animate={{
                    top: ['10%', '90%'],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-viridia rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20" data-id="element-1380"></motion.div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Custom Automations? */}
      <section className="py-20 bg-viridia-black" data-id="element-1381">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1382">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1383">
            <ScrollReveal data-id="element-1384">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1385">
                Off-the-Shelf Software Rarely Fits Perfectly
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1386">
                Most local businesses use 5-10 different software tools to run
                their operations. When these tools don't talk to each other,
                your team ends up doing manual data entry, leads fall through
                the cracks, and scaling becomes impossible.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1387">
                We build the "glue" that connects your systems. Whether it's a
                simple Zapier integration or a complex custom webhook, we
                engineer workflows that match exactly how you do business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1388">
                <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 flex items-start gap-3" data-id="element-1389">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1390" />
                  <div data-id="element-1391">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1392">
                      Without Automation
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1393">
                      Manual data entry, delayed follow-ups, human error.
                    </p>
                  </div>
                </div>
                <div className="bg-viridia-surface border border-viridia/30 rounded-xl p-4 flex items-start gap-3" data-id="element-1394">
                  <ZapIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1395" />
                  <div data-id="element-1396">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1397">
                      With Viridia
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1398">
                      Instant data sync, automated tasks, scalable operations.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <div className="relative" data-id="element-1399">
              <div className="absolute inset-0 bg-viridia/20 blur-[100px] rounded-full" data-id="element-1400"></div>
              <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 shadow-2xl" data-id="element-1401">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1402">
                  <WrenchIcon className="w-5 h-5 text-viridia" data-id="element-1403" /> Our Approach
                </h3>
                <ul className="space-y-6" data-id="element-1404">
                  <li className="flex items-start gap-4" data-id="element-1405">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1406">
                      1
                    </div>
                    <div data-id="element-1407">
                      <h4 className="text-white font-bold mb-1" data-id="element-1408">
                        Process Audit
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1409">
                        We map out your current manual process and identify the
                        bottlenecks costing you time.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1410">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1411">
                      2
                    </div>
                    <div data-id="element-1412">
                      <h4 className="text-white font-bold mb-1" data-id="element-1413">
                        Architecture Design
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1414">
                        We design a robust, scalable system architecture using
                        the right tools (APIs, Webhooks, Zapier, Make).
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1415">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1416">
                      3
                    </div>
                    <div data-id="element-1417">
                      <h4 className="text-white font-bold mb-1" data-id="element-1418">
                        Build & Test
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1419">
                        We build the automation, run extensive edge-case
                        testing, and ensure data integrity.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1420">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1421">
                      4
                    </div>
                    <div data-id="element-1422">
                      <h4 className="text-white font-bold mb-1" data-id="element-1423">
                        Deployment & Monitoring
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1424">
                        We launch the system and provide ongoing monitoring to
                        ensure it runs flawlessly 24/7.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Custom Makes Sense */}
      <section className="py-20 bg-viridia-surface" data-id="element-1425">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1426">
          <ScrollReveal data-id="element-1427">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1428">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1429">
                When Do You Need Custom Automations?
              </h2>
              <p className="text-gray-400" data-id="element-1430">
                Our standard packages cover 90% of local business needs. Custom
                automations are for the 10% that makes your business unique.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1431">
            <ScrollReveal data-id="element-1432">
              <div className="space-y-6" data-id="element-1433">
                <div className="flex items-start gap-4" data-id="element-1434">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1435">
                    <LayersIcon className="w-5 h-5 text-viridia" data-id="element-1436" />
                  </div>
                  <div data-id="element-1437">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1438">
                      Disconnected Software Stack
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1439">
                      Your field service software (like ServiceTitan or Jobber)
                      doesn't talk to your CRM or accounting software, forcing
                      you to enter data twice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1440">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1441">
                    <AlertCircleIcon className="w-5 h-5 text-viridia" data-id="element-1442" />
                  </div>
                  <div data-id="element-1443">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1444">
                      Complex Approval Processes
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1445">
                      Quotes over a certain amount need manager approval, or
                      specific job types need to be routed to specific
                      technicians based on logic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1446">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1447">
                    <DatabaseIcon className="w-5 h-5 text-viridia" data-id="element-1448" />
                  </div>
                  <div data-id="element-1449">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1450">
                      Heavy Admin Burden
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1451">
                      Your team spends hours every week copy-pasting information
                      from emails into spreadsheets or generating manual
                      reports.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1452">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1453">
                <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-1454">
                  The Cost of Manual Work
                </h3>
                <div className="space-y-6" data-id="element-1455">
                  <div data-id="element-1456">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1457">
                      <span className="text-gray-400" data-id="element-1458">Manual Data Entry</span>
                      <span className="text-red-400" data-id="element-1459">High Error Rate</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1460">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                      width: '85%'
                    }} data-id="element-1461"></div>
                    </div>
                  </div>
                  <div data-id="element-1462">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1463">
                      <span className="text-gray-400" data-id="element-1464">Information Silos</span>
                      <span className="text-yellow-400" data-id="element-1465">Slow Handoffs</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1466">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1467"></div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-800" data-id="element-1468">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1469">
                      <span className="text-white font-medium" data-id="element-1470">
                        Automated System
                      </span>
                      <span className="text-viridia font-medium" data-id="element-1471">
                        Instant & Accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1472">
                      <div className="bg-viridia h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1473"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-viridia-surface" data-id="element-1474">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1475">
          <ScrollReveal data-id="element-1476">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1477">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1478">
                Examples of Custom Workflows
              </h2>
              <p className="text-gray-400" data-id="element-1479">
                If it has an API, we can connect it. Here are a few examples of
                systems we can build.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1480">
            {[{
            title: 'Intelligent Lead Routing',
            desc: 'Web form submissions are analyzed by AI. High-value commercial leads are texted directly to the owner, while residential leads go to the standard sales queue.',
            icon: GitMergeIcon
          }, {
            title: 'Cross-Platform Syncing',
            desc: 'When a job is marked "Complete" in Jobber, automatically generate an invoice in QuickBooks and send a review request via Viridia CRM.',
            icon: WorkflowIcon
          }, {
            title: 'AI Call Summaries to Slack',
            desc: "After every recorded sales call, AI generates a 3-bullet summary of the customer's needs and posts it to a Slack channel for the fulfillment team.",
            icon: MessageSquareIcon
          }, {
            title: 'Automated Reporting',
            desc: 'Pull data from Google Ads, Facebook Ads, and your CRM into a custom Google Sheet that updates daily without manual export/import.',
            icon: FileTextIcon
          }, {
            title: 'Inventory Alerts',
            desc: 'When a specific service is booked, automatically check inventory levels in your management software and alert the purchasing manager if parts are needed.',
            icon: AlertCircleIcon
          }, {
            title: 'Custom Webhooks',
            desc: 'Need your proprietary internal software to trigger actions in modern marketing tools? We build the middleware to make it happen.',
            icon: SettingsIcon
          }].map((example, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1481">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full hover:border-viridia/30 transition-colors" data-id="element-1482">
                  <div className="w-10 h-10 rounded bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4" data-id="element-1483">
                    <example.icon className="w-5 h-5 text-viridia" data-id="element-1484" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1485">
                    {example.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1486">
                    {example.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1487">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1488">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1489">
            <ScrollReveal data-id="element-1490">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1491">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1492">
                Our custom automation services are designed for businesses with
                established operations that need to scale without adding more
                administrative headcount.
              </p>
              <ul className="space-y-4" data-id="element-1493">
                <li className="flex items-start gap-3" data-id="element-1494">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1495" />
                  <div data-id="element-1496">
                    <h4 className="font-bold text-white" data-id="element-1497">
                      Growing Contractors
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1498">
                      HVAC, painters, landscapers, window installers, and other
                      contractors that have outgrown manual processes and need
                      their software stack to work together seamlessly.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1499">
                  <UsersIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1500" />
                  <div data-id="element-1501">
                    <h4 className="font-bold text-white" data-id="element-1502">
                      Teams with Admin Bottlenecks
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1503">
                      If your office staff spends hours copying data between
                      systems, you need custom automation.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-1504">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1505">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1506">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-1507">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1508">
                    <div className="font-bold text-white mb-1" data-id="element-1509">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1510">
                      Our flagship system for instant lead response and booking.
                    </div>
                  </Link>
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1511">
                    <div className="font-bold text-white mb-1" data-id="element-1512">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1513">
                      Centralize your operations with our robust CRM platform.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1514">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1515">
          <ScrollReveal data-id="element-1516">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1517">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1518">
              Everything you need to know about our custom automation services.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1519">
          <ScrollReveal delay={0.2} data-id="element-1520">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1521">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1522" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1523">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1524">
          <ScrollReveal data-id="element-1525">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1526">
              Let's Map Out Your System
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-1527">
              Book a technical scoping call. We'll review your current software
              stack and identify exactly where automation can save you time and
              money.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1528">
              Book a Scoping Call
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}